import React from 'react';
import PaywallOffer, { paywallStateFor, type SignedOutUnlock } from '../PaywallOffer';
import type { ComplianceCheck, SalesReportSummary } from '../../utils/types';
import type { TeaserCtaSlot } from './ReportTeaser';
import { formatPriceFromCents } from '../../utils/price';
import { isCleanReport } from '../../utils/salesReport';

export interface OwnerUnlockCtaProps {
  summary: SalesReportSummary;
  reportId?: string | null;
  userId?: string | null;
  isSignedIn?: boolean;
  authEnabled?: boolean;
  balance?: number | null;
  /** See LockedReportView's packPrice: no default, on purpose (#970). */
  packPrice?: number;
  creditsPerPurchase?: number;
  onRunFullAudit?: (url: string) => Promise<void> | void;
  signInButton?: React.ReactNode;
  gpcOnly?: boolean;
  onUnlocked?: () => void;
  signedOutUnlock?: SignedOutUnlock;
  /** #2046: consumer copy in the unlock offer (see PaywallOffer). */
  consumer?: boolean;
  /** The owner's checks, the same list the teaser's "What we found" reads. */
  findings?: ComplianceCheck[];
}

const UNLOCK_ANCHOR = 'teaser-unlock';

/**
 * The owner's (or the signed-out claim holder's) CTA slot (#1807). The close
 * slot is the PaywallOffer block LockedReportView rendered before the teaser
 * was shared. The hero slot only scrolls to it: a second PaywallOffer would
 * put two "Unlock this report" buttons on one page.
 */
export function ownerUnlockCta(props: OwnerUnlockCtaProps): (where: TeaserCtaSlot) => React.ReactNode {
  const {
    summary, reportId, userId, isSignedIn, authEnabled, balance, packPrice, creditsPerPurchase,
    onRunFullAudit, signInButton, gpcOnly = false, onUnlocked, signedOutUnlock, consumer = false, findings,
  } = props;
  // Pack pricing and balance come from the same GET /api/credits response, so
  // they resolve together: pricing being present is exactly the signal that
  // the account was actually read (#970). Without it, `balance` arrives as
  // `null`, indistinguishable from a genuine unlimited account, and this
  // view would tell a free user they have unlimited access, then 402 them
  // the moment they act on it.
  const creditsResolved = packPrice !== undefined && creditsPerPurchase !== undefined;
  const paywallState = paywallStateFor({ authEnabled, isSignedIn, balance, creditsResolved });
  // #1762: only a full audit can be unlocked in place. The lane comes from
  // the summary, never from which sections ran: a default-mode lite scan runs
  // three of the four and is still a lite report the unlock route refuses.
  // A summary without `kind` (a legacy row, or an older API) keeps the
  // "Run full audit" path.
  const unlockInPlace = summary.kind === 'full' && !gpcOnly;
  // A signed-out visitor has no credit info, so packPrice is unknown here.
  // The summary carries the price the unlock checkout charges (the same
  // CREDIT_PACK_PRICE_CENTS the signed-in path reads), so the signed-out
  // unlock names it instead of asking for an unnamed amount.
  const signedOutPrice = paywallState === 'signed-out' && unlockInPlace && signedOutUnlock
    ? packPrice ?? summary.packPrice
    : undefined;
  // Nothing failed and nothing to review: the offer sells no fixes (the
  // teaser's close list makes the same call through the same predicate).
  const clean = isCleanReport(summary.sections, findings);
  const heroLabel = signedOutPrice !== undefined
    ? `Get the full report · ${formatPriceFromCents(signedOutPrice)}`
    : 'Get the full report';

  return function renderOwnerUnlockCta(where) {
    return where === 'hero' ? (
      <button
        type="button"
        className="sales-cta no-print"
        onClick={() => document.getElementById(UNLOCK_ANCHOR)?.scrollIntoView({ behavior: 'smooth' })}
      >
        {heroLabel}
      </button>
    ) : (
      <div id={UNLOCK_ANCHOR}>
        <PaywallOffer
          state={paywallState}
          userId={userId}
          // has-credits shows the full URL being audited (pre-refactor behaviour);
          // every other state shows the bare domain. Do not collapse this back to
          // one expression: the two branches intentionally read different fields
          // of `summary` (see issue #944 fix-round-1).
          target={paywallState === 'has-credits' ? summary.url : summary.domain}
          balance={balance}
          packPrice={signedOutPrice ?? packPrice}
          creditsPerPurchase={creditsPerPurchase}
          onRunFullAudit={() => onRunFullAudit?.(summary.url)}
          signInButton={signInButton}
          context={unlockInPlace ? 'unlock' : 'report'}
          reportId={reportId}
          onUnlocked={onUnlocked}
          signedOutUnlock={signedOutUnlock}
          consumer={consumer}
          clean={clean}
        />
      </div>
    );
  };
}
